import { createSlice } from "@reduxjs/toolkit";
import * as persistenceActionTypes from "store/actions/persistence/persistenceActionTypes";

const initialState = { rows: [] };

// Per-$1,000 vs per-1 bases. `per` drives the rate calculation; `perLabel` is
// how the basis is written on declarations pages. Sales/payroll/cost are
// monetary exposures so they read "per $1,000"; area and admissions are counted
// in square feet and admissions, so they read "per 1,000" with no dollar sign.
export const PREMIUM_BASIS_OPTIONS = [
  { value: "s", label: "Gross Sales", per: 1000, perLabel: "per $1,000" },
  { value: "p", label: "Payroll", per: 1000, perLabel: "per $1,000" },
  { value: "c", label: "Total Cost", per: 1000, perLabel: "per $1,000" },
  { value: "a", label: "Area", per: 1000, perLabel: "per 1,000" },
  { value: "m", label: "Admissions", per: 1000, perLabel: "per 1,000" },
  { value: "u", label: "Units", per: 1, perLabel: "per 1" },
  { value: "e", label: "Each", per: 1, perLabel: "per 1" },
];

// The single spelling of a basis used everywhere it is shown to a user or
// printed on a document: "(s) Gross Sales (per $1,000)". Unknown codes render
// as an empty string rather than a half-built label.
export const formatPremiumBasis = (premiumBasis) => {
  const opt = PREMIUM_BASIS_OPTIONS.find((o) => o.value === premiumBasis);
  if (!opt) return "";
  return `(${opt.value}) ${opt.label} (${opt.perLabel})`;
};

// Rate = advance premium / exposure, scaled by the basis-specific divisor.
// For s/p/c/a/m the doc renders rates "per $1,000" so we multiply by 1000.
// For u/e the rate is per-1 (no scaling).
export const computeRate = ({
  advancePremium,
  exposureAmount,
  premiumBasis,
}) => {
  // Treat null/undefined as "no value entered yet" rather than zero.
  // Number(null) returns 0 which would otherwise silently render a "rate" of 0.
  if (advancePremium == null || exposureAmount == null) {
    return null;
  }
  const premium = Number(advancePremium);
  const exposure = Number(exposureAmount);
  if (
    !Number.isFinite(premium) ||
    !Number.isFinite(exposure) ||
    exposure === 0
  ) {
    return null;
  }
  const opt = PREMIUM_BASIS_OPTIONS.find((o) => o.value === premiumBasis);
  const per = opt?.per ?? 1000;
  return (premium / exposure) * per;
};

// Persisted alongside the row inputs so doc-gen templates and reports can read
// `cls.loss_rate` directly without recomputing from premium and exposure.
const withDerivedLossRate = (row) => ({
  ...row,
  loss_rate: computeRate({
    advancePremium: row.advancePremium,
    exposureAmount: row.exposureAmount,
    premiumBasis: row.premiumBasis,
  }),
});

const glClassificationsSlice = createSlice({
  name: "glClassifications",
  initialState,
  reducers: {
    setGLClassifications: (_, action) => {
      const values = action?.payload ?? {};
      if (values?.constructor?.name !== "Object") {
        return;
      }
      return { ...initialState, ...values };
    },
    addRow: (state, action) => {
      const row = action.payload;
      return {
        ...state,
        rows: [...(state.rows ?? []), withDerivedLossRate(row)],
      };
    },
    updateRow: (state, action) => {
      const { id, values } = action.payload;
      return {
        ...state,
        rows: (state.rows ?? []).map((r) =>
          r.id === id ? withDerivedLossRate({ ...r, ...values }) : r
        ),
      };
    },
    removeRow: (state, action) => {
      const id = action.payload;
      return {
        ...state,
        rows: (state.rows ?? []).filter((r) => r.id !== id),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      persistenceActionTypes.LOAD_SUBMISSION_SUCCEEDED,
      (_, action) =>
        action?.payload?.data?.state?.glClassifications ?? initialState
    );
  },
});

export const selectGLClassifications = (state) =>
  state?.glClassifications ?? initialState;

export const selectRows = (state) => state?.glClassifications?.rows ?? [];

// Resolves the raw rows into the shape doc-gen / reports consume, mirroring
// `selectResolvedGlExposures`: camelCase inputs are mapped to the snake_case
// field names templates address (`cls.class_code`, `cls.basic_limits_loss_cost`,
// ...). `loss_rate` is already derived on the row and rides along in `...rest`.
// This is what gets pulled into the resolved submission, so templates read it
// through the submission-level `GL_CLASSIFICATIONS` alias rather than a bespoke
// doc-gen context variable.
//
// `premium_basis` stays the raw code for anything that keys off it;
// `premium_basis_display` carries the printable spelling, because the label and
// per-unit text live only in `PREMIUM_BASIS_OPTIONS` here and would otherwise
// have to be duplicated in every declarations-page template.
export const selectResolvedGlClassifications = (state) =>
  selectRows(state).map(
    ({
      id: _id,
      classification,
      premiumBasis,
      exposureAmount,
      advancePremium,
      ...rest
    }) => ({
      ...rest,
      class_code: classification,
      premium_basis: premiumBasis,
      premium_basis_display: formatPremiumBasis(premiumBasis),
      exposure_amount: exposureAmount,
      basic_limits_loss_cost: advancePremium,
    })
  );

export const { addRow, updateRow, removeRow } = glClassificationsSlice.actions;

export default glClassificationsSlice;
